import { useState, useEffect, useCallback } from 'react';
import { RankingService } from '@/services/rankingService';

/**
 * Custom hook to manage ranking/standings state
 * @param {Object} options - Configuration options
 * @param {Array} options.leagues - Available leagues (optional)
 * @param {Function} options.onLeagueChange - Callback when league changes (optional)
 * @returns {Object} Ranking state and operations
 */
export const useRanking = (options = {}) => {
  const { leagues = [], onLeagueChange } = options;

  const [currentLeagueId, setCurrentLeagueId] = useState(null);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('total');
  const [availableTabs, setAvailableTabs] = useState([]);
  const [rankingData, setRankingData] = useState({});

  /**
   * Select a league and fetch its rankings
   */
  const selectLeague = useCallback((league) => {
    if (league && league.id !== currentLeagueId) {
      setCurrentLeagueId(league.id);
      if (onLeagueChange) {
        onLeagueChange(league);
      }
    }
  }, [currentLeagueId, onLeagueChange]);

  /**
   * Fetch rankings data for a specific league
   */
  const fetchRankings = useCallback(async () => {
    if (!currentLeagueId) {
      setStandings(null);
      setAvailableTabs([]);
      setRankingData({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await RankingService.getRankingsByLeagueId(currentLeagueId);
      
      if (data) {
        setStandings(data);
        
        const parsed = RankingService.parseRankingData(data);
        setAvailableTabs(parsed.availableTabs);
        setRankingData(parsed.data);

        if (parsed.availableTabs.length > 0) {
          const defaultTab = parsed.availableTabs.includes('totalStandings')
            ? 'totalStandings'
            : parsed.availableTabs[0];
          setActiveTab(defaultTab);
        }
      } else {
        setStandings(null);
        setAvailableTabs([]);
        setRankingData({});
      }
    } catch (err) {
      console.error('Error fetching rankings:', err);
      setError(err.message || 'Không thể tải dữ liệu bảng xếp hạng');
      setStandings(null);
      setAvailableTabs([]);
      setRankingData({});
    } finally {
      setLoading(false);
    }
  }, [currentLeagueId]);

  /**
   * Change active tab
   * @param {string} tab - Tab key to switch to
   */
  const changeTab = useCallback(
    (tab) => {
      if (availableTabs.includes(tab)) {
        setActiveTab(tab);
      }
    },
    [availableTabs]
  );

  /**
   * Get current tab data
   */
  const getCurrentData = useCallback(() => {
    return rankingData[activeTab] || [];
  }, [rankingData, activeTab]);

  useEffect(() => {
    if (leagues.length > 0 && !currentLeagueId) {
      const firstLeague = leagues[0];
      selectLeague(firstLeague);
    }
  }, [leagues, currentLeagueId, selectLeague]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  return {
    currentLeagueId,
    standings,
    loading,
    error,
    activeTab,
    availableTabs,
    currentData: getCurrentData(),

    selectLeague,
    changeTab,
    refetch: fetchRankings,
  };
};
